import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import Lightbox from '../components/Lightbox';
import TileVideo from '../components/gallery/TileVideo';
import { useSettings } from '../context/SettingsContext';
import { fallbackProducts } from '../prerenderData';
import { galleryProjects } from '../data/projects';
import './GalleryPage.css';

// /gallery - every past project: photos and short video loops from real jobs,
// newest first as ordered in the admin. Each awning's product page links here
// (components/gallery/GalleryLink), pre-filtered to that awning with
// ?product=<slug>; the filter chips switch between products or show all.
//
// The project list is the `projects` site setting, read through
// data/projects.js - the same list the homepage's Featured Projects shows a
// selection of.

// Product names for the filter and the project's product link. The build-time
// snapshot is enough: a product's name rarely changes, and this keeps the page
// free of a second fetch.
const PRODUCT_NAMES = Object.fromEntries((fallbackProducts || []).map((p) => [p.slug, p.name]));
const productName = (slug) => PRODUCT_NAMES[slug] || '';

// One project's tiles, in viewing order: the lead photo, then its videos (the
// motion is the hook), then the rest of the photos.
function projectMedia(p) {
  const alt = (label, i) => `${p.title}${label ? ` - ${label}` : ` - photo ${i + 1}`}`;
  const photo = (ph, i) => ({ type: 'image', src: ph.url, focus: ph.focus, label: ph.label, alt: alt(ph.label, i) });
  const video = (v, i) => ({ type: 'video', src: v.mp4, poster: v.poster, label: '', alt: `${p.title} - video ${i + 1}` });
  const photos = p.photos.map(photo);
  const videos = p.videos.map(video);
  return photos.length ? [photos[0], ...videos, ...photos.slice(1)] : videos;
}

function ProjectBlock({ project, onOpen }) {
  const media = projectMedia(project);
  const name = productName(project.product);
  const meta = [project.location, project.colour, project.size].filter(Boolean);
  return (
    <article className="gal-project" id={project.id}>
      <header className="gal-project-head">
        <h2 className="gal-project-title">{project.title}</h2>
        {(meta.length > 0 || name) && (
          <p className="gal-project-meta">
            {meta.map((m) => <span key={m} className="gal-chip">{m}</span>)}
            {name && <Link to={`/products/${project.product}`} className="gal-product-link">{name} →</Link>}
          </p>
        )}
        {project.caption && <p className="gal-project-caption">{project.caption}</p>}
      </header>
      <ul className="gal-grid">
        {media.map((m, i) => (
          <li className="gal-tile" key={m.src}>
            {m.type === 'video'
              ? <TileVideo src={m.src} poster={m.poster} label={m.alt} />
              : <img src={m.src} alt={m.alt} loading="lazy" width={800} height={600} style={{ objectPosition: `50% ${m.focus}%` }} />}
            {m.label && <span className="gal-tile-label">{m.label}</span>}
            <button type="button" className="gal-tile-open" onClick={() => onOpen(media, i, project.title)} aria-label={`View larger: ${m.alt}`} />
          </li>
        ))}
      </ul>
    </article>
  );
}

export default function GalleryPage() {
  const settings = useSettings();
  const [params, setParams] = useSearchParams();
  const [viewer, setViewer] = useState(null); // { items, index, title }

  const projects = galleryProjects(settings);
  const filters = [...new Set(projects.map((p) => p.product).filter((s) => s && productName(s)))];
  const active = filters.includes(params.get('product')) ? params.get('product') : '';
  const shown = active ? projects.filter((p) => p.product === active) : projects;

  const choose = (slug) => {
    const next = new URLSearchParams(params);
    if (slug) next.set('product', slug); else next.delete('product');
    setParams(next, { replace: true });
  };

  const open = (media, index, title) => setViewer({
    title,
    index,
    items: media.map((m) => ({ type: m.type, src: m.src, poster: m.poster, alt: m.alt, caption: m.label ? `${title} - ${m.label}` : title })),
  });

  return (
    <div className="gal-page">
      <SEO
        title="Project Gallery - Awnings and Pergolas"
        description="Photos and videos of ASG awnings and pergolas on real jobs around Melbourne, and test-run in our workshop before delivery."
        keywords="awning gallery, awning projects Melbourne, retractable awning photos, ASG Shading projects"
        canonical="/gallery"
        jsonLd={[SEO.breadcrumbSchema([{ name: 'Gallery' }]), SEO.webPageSchema({ type: 'CollectionPage', name: 'Project Gallery', description: 'Past ASG Shading awning and pergola projects.', url: '/gallery' })]}
      />
      <section className="gal-hero">
        <div className="gal-hero-content">
          <p className="gal-label">Gallery</p>
          <h1 className="gal-title">Our projects</h1>
          <p className="gal-subtitle">
            Awnings and pergolas we have supplied, photographed on the job and in our workshop
            before delivery.
          </p>
        </div>
      </section>

      <Breadcrumbs items={[{ name: 'Gallery' }]} />

      <section className="gal-main">
        {filters.length > 1 && (
          <div className="gal-filters" role="group" aria-label="Show projects for">
            <button type="button" className={`gal-filter${active ? '' : ' gal-filter--on'}`} aria-pressed={!active} onClick={() => choose('')}>
              All projects
            </button>
            {filters.map((slug) => (
              <button key={slug} type="button" className={`gal-filter${active === slug ? ' gal-filter--on' : ''}`} aria-pressed={active === slug} onClick={() => choose(slug)}>
                {productName(slug)}
              </button>
            ))}
          </div>
        )}

        {shown.length === 0 ? (
          <p className="gal-empty">Photos from our latest jobs are on their way.</p>
        ) : (
          <div className="gal-list">
            {shown.map((p) => <ProjectBlock key={p.id} project={p} onOpen={open} />)}
          </div>
        )}

        <div className="gal-cta">
          <h2 className="gal-cta-title">Planning one of your own?</h2>
          <p className="gal-cta-text">Send us a photo of the space and we will tell you which awning suits it and what it costs.</p>
          <div className="gal-cta-actions">
            <Link to="/request-quote" className="asg-btn-primary">Get a quote</Link>
            <Link to="/products/awnings" className="asg-btn-secondary">See the awnings</Link>
          </div>
        </div>
      </section>

      {viewer && (
        <Lightbox
          items={viewer.items}
          index={viewer.index}
          title={viewer.title}
          onIndex={(index) => setViewer((v) => ({ ...v, index }))}
          onClose={() => setViewer(null)}
        />
      )}
    </div>
  );
}
